import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Search,
  Filter,
  RefreshCw,
  FileText,
  Pause,
  Play,
  AlertCircle,
  CheckCircle,
  Clock,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { invoicesApi, Invoice, InvoiceStats, ListInvoicesParams } from '../api/invoices.api';
import { sitesApi } from '../api/sites.api';
import { InvoiceDetailsDrawer } from './InvoiceDetailsPage';
import { InvoicePdfActions } from '../components/InvoicePdfActions';
import { useSyncStatus } from '../hooks/useSyncStatus';
import { APP_MAIN_OVERLAY_CLASS, APP_PAGE_WRAPPER_CLASS } from '../lib/layout';

// Due Status Badge Component
function DueStatusBadge({ status, description }: { status: string; description: string }) {
  const styles: Record<string, string> = {
    paid: 'bg-green-100 text-green-800 border-green-200',
    due_today: 'bg-orange-100 text-orange-800 border-orange-200',
    overdue: 'bg-red-100 text-red-800 border-red-200',
    upcoming: 'bg-blue-100 text-blue-800 border-blue-200',
    unknown: 'bg-gray-100 text-gray-600 border-gray-200',
  };

  const icons: Record<string, React.ReactNode> = {
    paid: <CheckCircle className="w-3 h-3" />,
    due_today: <Clock className="w-3 h-3" />,
    overdue: <AlertTriangle className="w-3 h-3" />,
    upcoming: <Clock className="w-3 h-3" />,
    unknown: <AlertCircle className="w-3 h-3" />,
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
        styles[status] || styles.unknown
      }`}
      title={description}
    >
      {icons[status] || icons.unknown}
      {description}
    </span>
  );
}

// Payment Status Badge Component
function PaymentStatusBadge({ status }: { status: string }) {
  const labels: Record<string, string> = {
    unpaid: 'Unpaid',
    partially_paid: 'Partially Paid',
    paid: 'Paid',
    cancelled: 'Cancelled',
    unknown: 'Unknown',
  };

  const styles: Record<string, string> = {
    unpaid: 'bg-red-50 text-red-700',
    partially_paid: 'bg-yellow-50 text-yellow-700',
    paid: 'bg-green-50 text-green-700',
    cancelled: 'bg-gray-50 text-gray-600',
    unknown: 'bg-gray-50 text-gray-500',
  };

  return (
    <span
      className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
        styles[status] || styles.unknown
      }`}
    >
      {labels[status] || 'Unknown'}
    </span>
  );
}

// Stats Card Component
function StatsCard({
  title,
  value,
  icon: Icon,
  color,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export function InvoicesPage() {
  const queryClient = useQueryClient();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // Filters and pagination
  const [filters, setFilters] = useState<ListInvoicesParams>({
    page: 1,
    per_page: 50,
  });
  const [searchInput, setSearchInput] = useState('');

  const { isSyncing } = useSyncStatus();

  const { data: sitesData } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesApi.list(),
  });

  // Fetch stats
  const { data: stats, isLoading: statsLoading } = useQuery<InvoiceStats>({
    queryKey: ['invoices', 'stats'],
    queryFn: invoicesApi.getStats,
  });

  // Fetch invoices
  const {
    data: invoicesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['invoices', 'list', filters],
    queryFn: () => invoicesApi.listInvoices(filters),
    placeholderData: (previousData) => previousData,
    refetchInterval: isSyncing ? 3000 : false,
  });

  // Toggle pause mutation
  const togglePauseMutation = useMutation({
    mutationFn: (invoiceId: number) => invoicesApi.togglePause(invoiceId),
    onSuccess: (data) => {
      toast.success(data.reminder_paused ? 'Reminders paused' : 'Reminders resumed');
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: () => {
      toast.error('Failed to toggle reminder pause');
    },
  });

  // Handle search with debounce
  const handleSearch = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      search: searchInput || undefined,
      page: 1,
    }));
  }, [searchInput]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  // Handle filter change
  const handleFilterChange = (
    key: keyof ListInvoicesParams,
    value: string | number | undefined
  ) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' || value === undefined || value === '' ? undefined : value,
      page: 1,
    }));
  };

  // Open invoice details
  const openInvoiceDetails = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setIsDrawerOpen(true);
  };

  // Format currency
  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (date: string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const invoices = invoicesData?.invoices || [];
  const pagination = invoicesData?.pagination;

  return (
    <div className={APP_PAGE_WRAPPER_CLASS}>
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600">Manage and monitor invoice status</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <StatsCard
          title="Total Invoices"
          value={statsLoading ? '-' : stats?.total || 0}
          icon={FileText}
          color="bg-blue-50 text-blue-600"
        />
        <StatsCard
          title="Due Today"
          value={statsLoading ? '-' : stats?.due_today || 0}
          icon={Clock}
          color="bg-orange-50 text-orange-600"
        />
        <StatsCard
          title="Overdue"
          value={statsLoading ? '-' : stats?.overdue || 0}
          icon={AlertTriangle}
          color="bg-red-50 text-red-600"
        />
        <StatsCard
          title="Paid"
          value={statsLoading ? '-' : stats?.paid || 0}
          icon={CheckCircle}
          color="bg-green-50 text-green-600"
        />
        <StatsCard
          title="Outstanding"
          value={
            statsLoading
              ? '-'
              : formatCurrency(stats?.total_outstanding || 0)
          }
          icon={AlertCircle}
          color="bg-purple-50 text-purple-600"
        />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by invoice #, subscriber, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Due Status Filter */}
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-400" />
            <select
              value={filters.due_status || 'all'}
              onChange={(e) => handleFilterChange('due_status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Due Status</option>
              <option value="due_today">Due Today</option>
              <option value="overdue">Overdue</option>
              <option value="upcoming">Upcoming</option>
              <option value="paid">Paid</option>
            </select>
          </div>

          {/* Site filter */}
          <select
            value={filters.site_id ?? ''}
            onChange={(e) =>
              handleFilterChange(
                'site_id',
                e.target.value ? Number(e.target.value) : undefined
              )
            }
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 min-w-[160px]"
          >
            <option value="">All sites</option>
            {sitesData?.sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name} (CRM {site.crmSiteId})
              </option>
            ))}
          </select>

          {/* Payment Status Filter */}
          <div className="flex items-center gap-2">
            <select
              value={filters.payment_status || 'all'}
              onChange={(e) => handleFilterChange('payment_status', e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Payment Status</option>
              <option value="unpaid">Unpaid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="paid">Paid</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <RefreshCw className="w-8 h-8 mx-auto text-gray-400 animate-spin" />
            <p className="mt-2 text-gray-600">Loading invoices...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <AlertCircle className="w-8 h-8 mx-auto text-red-500" />
            <p className="mt-2 text-red-600">Failed to load invoices</p>
            <button
              onClick={() => refetch()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : invoices.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 mx-auto text-gray-300" />
            <p className="mt-2 text-gray-600">No invoices found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Subscriber
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Site
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Due Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr
                      key={invoice.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => openInvoiceDetails(invoice)}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{invoice.invoice_number}</p>
                          <p className="text-sm text-gray-500">{formatDate(invoice.invoice_date)}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{invoice.subscriber.name}</p>
                          <p className="text-sm text-gray-500">{invoice.subscriber.mobile}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="font-medium text-gray-900">
                            {invoice.site.name || `Site ${invoice.site.crmSiteId}`}
                          </p>
                          <p className="text-xs text-gray-500 font-mono">
                            CRM {invoice.site.crmSiteId}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{formatDate(invoice.due_date)}</td>
                      <td className="px-4 py-3 text-right">
                        <div>
                          <p className="font-medium text-gray-900">
                            {formatCurrency(invoice.amount_total)}
                          </p>
                          {invoice.amount_paid > 0 && (
                            <p className="text-sm text-gray-500">
                              Paid: {formatCurrency(invoice.amount_paid)}
                            </p>
                          )}
                          {invoice.amount_outstanding > 0 && (
                            <p className="text-sm text-red-600">
                              Due: {formatCurrency(invoice.amount_outstanding)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <PaymentStatusBadge status={invoice.payment_status} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <DueStatusBadge
                          status={invoice.due_status}
                          description={invoice.due_status_description}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <InvoicePdfActions
                            invoiceId={invoice.id}
                            pdfUrl={invoice.pdf_url}
                            hasPdf={invoice.has_pdf}
                            compact
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePauseMutation.mutate(invoice.id);
                            }}
                            disabled={togglePauseMutation.isPending}
                            className={`p-1.5 rounded disabled:opacity-50 ${
                              invoice.reminder_paused
                                ? 'text-orange-600 hover:bg-orange-50'
                                : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
                            }`}
                            title={invoice.reminder_paused ? 'Resume reminders' : 'Pause reminders'}
                          >
                            {invoice.reminder_paused ? (
                              <Play className="w-4 h-4" />
                            ) : (
                              <Pause className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Showing {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
                  {Math.min(pagination.current_page * pagination.per_page, pagination.total_items)} of{' '}
                  {pagination.total_items} invoices
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(pagination.current_page - 1)}
                    disabled={pagination.current_page === 1}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">
                    Page {pagination.current_page} of {pagination.total_pages}
                  </span>
                  <button
                    onClick={() => handlePageChange(pagination.current_page + 1)}
                    disabled={pagination.current_page === pagination.total_pages}
                    className="p-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      </div>

      <InvoiceDetailsDrawer
        invoice={selectedInvoice}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedInvoice(null);
        }}
        overlayClass={APP_MAIN_OVERLAY_CLASS}
      />
    </div>
  );
}

export default InvoicesPage;
